import { TextStyle } from "react-native";
import { BoardValueColorRule } from "../config/companies/config";

const DEFAULT_ALERT_COLOR = "#d32f2f";

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const normalized = hex.trim().replace("#", "");
  const fullHex =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized;

  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) return null;

  const int = parseInt(fullHex, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
};

const mixRgb = (
  base: { r: number; g: number; b: number },
  target: { r: number; g: number; b: number },
  ratio: number,
) => {
  const clamped = Math.max(0, Math.min(1, ratio));
  return {
    r: Math.round(base.r * (1 - clamped) + target.r * clamped),
    g: Math.round(base.g * (1 - clamped) + target.g * clamped),
    b: Math.round(base.b * (1 - clamped) + target.b * clamped),
  };
};

const getLuminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
};

const getReadableBackground = (textColor: string): string | undefined => {
  const rgb = hexToRgb(textColor);
  if (!rgb) return undefined;

  const luminance = getLuminance(rgb);
  const mixed =
    luminance > 0.65
      ? mixRgb(rgb, { r: 0, g: 0, b: 0 }, 0.32)
      : mixRgb(rgb, { r: 255, g: 255, b: 255 }, 0.86);

  return `rgb(${mixed.r}, ${mixed.g}, ${mixed.b})`;
};

export const getBoardValueStyleByRules = (
  value: unknown,
  rules?: BoardValueColorRule[],
): TextStyle | undefined => {
  if (!rules || rules.length === 0) return undefined;

  const currentValue = String(value ?? "");
  for (const rule of rules) {
    const color =
      typeof rule === "string"
        ? rule === currentValue
          ? DEFAULT_ALERT_COLOR
          : undefined
        : rule.text === currentValue
          ? rule.color
          : undefined;

    if (!color) continue;

    return {
      color,
      backgroundColor: getReadableBackground(color),
      borderRadius: 6,
      overflow: "hidden",
      paddingHorizontal: 6,
      paddingVertical: 2,
    };
  }

  return undefined;
};
